/**
 * Ingredient checkbox handler
 *
 * Adds checkboxes before each ingredient list item and handles
 * checked state with strikethrough styling.
 */

/**
 * Create a checkbox element for an ingredient
 * @param {string} ingredientKey - Key in format "group_{groupIndex}_item_{itemIndex}"
 * @param {boolean} isChecked - Whether the checkbox should be checked
 * @param {Function} onToggle - Callback when checkbox is toggled
 * @return {HTMLElement}
 */
const createCheckbox = (ingredientKey, isChecked, onToggle) => {
  const checkbox = document.createElement('input')
  checkbox.type = 'checkbox'
  checkbox.className = 'mv-create-ingredient-checkbox'
  checkbox.checked = isChecked
  checkbox.setAttribute('data-ingredient-key', ingredientKey)
  checkbox.setAttribute('aria-label', 'Mark ingredient as gathered')

  checkbox.addEventListener('change', (e) => {
    onToggle(ingredientKey)
    updateIngredientStyle(e.target.closest('li'), e.target.checked)
  })

  return checkbox
}

/**
 * Update the visual style of an ingredient based on checked state
 * @param {HTMLElement} listItem - The li element
 * @param {boolean} isChecked - Whether the ingredient is checked
 */
const updateIngredientStyle = (listItem, isChecked) => {
  if (!listItem) return

  if (isChecked) {
    listItem.classList.add('mv-checklist-checked')
  } else {
    listItem.classList.remove('mv-checklist-checked')
  }
}

/**
 * Initialize ingredient checkboxes for a card
 * @param {HTMLElement} cardElement - The Create card container element
 * @param {Function} isIngredientChecked - Function to check if an ingredient is checked
 * @param {Function} toggleIngredient - Function to toggle an ingredient's state
 */
export const initIngredientCheckboxes = (
  cardElement,
  isIngredientChecked,
  toggleIngredient
) => {
  // Find all ingredient groups
  const ingredientGroups = cardElement.querySelectorAll(
    '.mv-create-ingredient-group[data-ingredient-group]'
  )

  ingredientGroups.forEach((group) => {
    const groupIndex = group.getAttribute('data-ingredient-group')

    // Find all ingredient list items within this group
    const ingredients = group.querySelectorAll('li[data-ingredient-index]')

    ingredients.forEach((ingredient) => {
      const itemIndex = ingredient.getAttribute('data-ingredient-index')
      const ingredientKey = `group_${groupIndex}_item_${itemIndex}`

      // Check if checkbox already exists (avoid duplicates)
      if (ingredient.querySelector('.mv-create-ingredient-checkbox')) {
        return
      }

      // Get current checked state
      const isChecked = isIngredientChecked(ingredientKey)

      // Create and insert checkbox
      const checkbox = createCheckbox(ingredientKey, isChecked, toggleIngredient)
      ingredient.insertBefore(checkbox, ingredient.firstChild)

      // Apply initial style if checked
      if (isChecked) {
        updateIngredientStyle(ingredient, true)
      }

      // Make the entire ingredient row clickable (except links)
      ingredient.style.cursor = 'pointer'
      ingredient.addEventListener('click', (e) => {
        // Don't toggle if clicking on a link or the checkbox itself
        if (e.target.tagName === 'A' || e.target === checkbox) {
          return
        }
        // Toggle the checkbox
        checkbox.checked = !checkbox.checked
        toggleIngredient(ingredientKey)
        updateIngredientStyle(ingredient, checkbox.checked)
      })
    })
  })
}

/**
 * Restore ingredient checkbox states from saved state
 * @param {HTMLElement} cardElement - The Create card container element
 * @param {Function} isIngredientChecked - Function to check if an ingredient is checked
 */
export const restoreIngredientStates = (cardElement, isIngredientChecked) => {
  const checkboxes = cardElement.querySelectorAll('.mv-create-ingredient-checkbox')

  checkboxes.forEach((checkbox) => {
    const ingredientKey = checkbox.getAttribute('data-ingredient-key')
    const isChecked = isIngredientChecked(ingredientKey)

    checkbox.checked = isChecked
    updateIngredientStyle(checkbox.closest('li'), isChecked)
  })
}

export default initIngredientCheckboxes
